import { Inject, Injectable } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { DOCUMENT } from '@angular/common';
import { filter, interval, Subject } from 'rxjs';
import { Router } from '@angular/router';
import { ToastService } from '@core/services/toast.service';

@Injectable({
  providedIn: 'root',
})
export class ServiceWorkerService {
  private deployingNewVersion = false;

  constructor(
    private swUpdate: SwUpdate,
    @Inject(DOCUMENT) private document: Document,
    private router: Router,
    private toast: ToastService
  ) {
    if (swUpdate.isEnabled) {
      const intervalTime = 1 * 60 * 1000;
      interval(intervalTime).subscribe(() =>
        swUpdate.checkForUpdate().catch((error) => {
          console.error('Error checking for updates:', error);
        })
      );

      swUpdate.unrecoverable.subscribe((event) => {
        console.error('Service worker unrecoverable state:', event);

        this.toast.error({
          summary: 'App Update Required',
          detail: 'The app needs to be reloaded to continue working properly.',
          sticky: true,
          key: 'unrecoverable-state',
        });

        setTimeout(() => {
          this.refresh();
        }, 3000);
      });

      this.checkForUpdates();
    }
  }

  public checkForUpdates(): void {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.swUpdate.versionUpdates
      .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
      .subscribe(async () => {
        if (!this.deployingNewVersion) {
          this.deployingNewVersion = true;

          if (this.router.url.includes('/auth')) {
            this.refresh();
          } else {
            this.notifyUpdateAvailable();
          }
        }
      });
  }

  async refresh(): Promise<void> {
    try {
      await this.swUpdate.activateUpdate();
      this.deployingNewVersion = false;
      window.location.reload();
    } catch (error) {
      console.error('Error activating update:', error);
      this.deployingNewVersion = false;
      window.location.reload();
    }
  }

  public manualCheckForUpdate(): Promise<boolean> {
    if (this.swUpdate.isEnabled) {
      return this.swUpdate.checkForUpdate();
    }
    return Promise.resolve(false);
  }

  private notifyUpdateAvailable(): void {
    this.toast.info({
      summary: 'Update Available',
      detail: 'A new version of the app is available. The app will reload automatically in 20 seconds.',
      sticky: true,
      key: 'update-available',
    });
  }
}
